import React, { useState, useEffect, useRef, useId, useCallback } from 'react';
import clsx from 'clsx';
import Button from '../Button/Button';
import InputField from '../InputField/InputField';
import { legacyMenuToSidebar } from './normalizeLegacyMenu';
import { z } from "zod";
import SmartImage from '../SmartImage/SmartImage'; // já existe no projeto

export interface SideBarItem {
  label: string;
  icon?: React.ReactNode;
  onClick?: () => void;
  to?: string;
  subItems?: SideBarItem[];
  badge?: React.ReactNode;
  badgeClassName?: string;
  loadSubItems?: () => Promise<SideBarItem[]>;
  [key: string]: unknown; // <-- Adicione esta linha
}

// Types para menu legado
export interface LegacyMenuItem {
  menu: string;
  id: string;
  [key: string]: unknown;
}

interface SideBarProps {
  open: boolean;
  onClose: () => void;
  items: SideBarItem[];
  theme?: 'light' | 'dark';
  linkComponent?: React.ComponentType<{ href: string; children: React.ReactNode; [key: string]: unknown }>;
  selectedMenu?: string; // <-- Adicionado aqui
  logoUrl?: string; // <-- adicione esta linha
  logoAlt?: string; // <-- adicione esta linha
}

/**
 * SideBar - Componente de menu lateral acessível e responsivo.
 *
 * Exibe um menu lateral com suporte a submenus, navegação por teclado, overlay com animação,
 * foco preso, fechamento por clique fora ou tecla ESC, e tema claro/escuro.
 *
 * @component
 * @param {Object} props - Propriedades do componente.
 * @param {boolean} props.open - Define se o sidebar está aberto.
 * @param {() => void} props.onClose - Função chamada ao fechar o sidebar.
 * @param {SideBarItem[]} props.items - Lista de itens do menu.
 * @param {'light' | 'dark'} [props.theme='light'] - Tema visual do sidebar.
 * @param {React.ComponentType<{ href: string; children: React.ReactNode; [key: string]: unknown }>} [props.linkComponent] - Componente customizado para links.
 *
 * @example
 * // Exemplo de uso básico
 * import SideBar from './SideBar';
 * import HomeIcon from './icons/HomeIcon';
 * import SettingsIcon from './icons/SettingsIcon';
 *
 * const items = [
 *   { label: 'Início', icon: <HomeIcon />, onClick: () => alert('Início') },
 *   {
 *     label: 'Configurações',
 *     icon: <SettingsIcon />,
 *     subItems: [
 *       { label: 'Perfil', onClick: () => alert('Perfil') },
 *       { label: 'Conta', onClick: () => alert('Conta') },
 *     ],
 *   },
 * ];
 *
 * <SideBar
 *   open={sidebarOpen}
 *   onClose={() => setSidebarOpen(false)}
 *   items={items}
 *   theme="dark"
 * />
 *
 * @example
 * // Exemplo com carregamento assíncrono de submenus
 * const items = [
 *   {
 *     label: 'Mais opções',
 *     loadSubItems: async () => [
 *       { label: 'Opção 1', onClick: () => alert('Opção 1') },
 *       { label: 'Opção 2', onClick: () => alert('Opção 2') },
 *     ],
 *   },
 * ];
 *
 * <SideBar open={open} onClose={close} items={items} />
 *
 * @example
 * // Exemplo com múltiplas instâncias (IDs únicos)
 * <SideBar open={open1} onClose={close1} items={items1} />
 * <SideBar open={open2} onClose={close2} items={items2} theme="dark" />
 *
 * @example
 * // Exemplo com dados genéricos normalizados
 * import { normalizeSidebarData } from './SideBar';
 * const rawData = [
 *   { nome: 'Página Inicial', rota: '/', filhos: [] },
 *   { nome: 'Sobre', rota: '/sobre', filhos: [] },
 * ];
 * const items = normalizeSidebarData(rawData, { labelKey: 'nome', toKey: 'rota', subItemsKey: 'filhos' });
 * <SideBar open={open} onClose={close} items={items} />
 *
 * @typedef {Object} SideBarItem
 * @property {string} label - Texto do item.
 * @property {React.ReactNode} [icon] - Ícone opcional.
 * @property {() => void} [onClick] - Função ao clicar.
 * @property {string} [to] - Rota/link do item.
 * @property {SideBarItem[]} [subItems] - Subitens do menu.
 * @property {React.ReactNode} [badge] - Badge/contador opcional.
 * @property {string} [badgeClassName] - Classe CSS para badge.
 * @property {() => Promise<SideBarItem[]>} [loadSubItems] - Função para carregar subitens sob demanda.
 */

// Função para detectar formato legado
function isLegacyMenu(data: unknown[]): boolean {
  return Array.isArray(data) &&
    data.length > 0 &&
    data[0] != null &&
    typeof data[0] === 'object' &&
    'menu' in data[0] &&
    'id' in data[0];
}

const SideBar: React.FC<SideBarProps> = ({
  open,
  onClose,
  items,
  theme = 'light',
  linkComponent,
  selectedMenu,
  logoUrl, // <-- adicione aqui
  logoAlt, // <-- adicione aqui
}) => {
  const [expanded, setExpanded] = useState<string[]>([]);
  const [search, setSearch] = useState('');
  const [loadingKeys, setLoadingKeys] = useState<string[]>([]);
  const [loadedSubItems, setLoadedSubItems] = useState<Record<string, SideBarItem[]>>({});
  const firstItemRef = useRef<HTMLButtonElement>(null);
  const sidebarRef = useRef<HTMLDivElement>(null);
  const titleId = useId();

  // Gera uma chave única para cada item/subitem baseado no caminho
  const getItemKey = (item: SideBarItem, parentKeys: string[]) =>
    [...parentKeys, item.label].join('>');

  // Função para filtrar itens e subitens pelo texto digitado (recursivo)
  const filterItems = (items: SideBarItem[], query: string): SideBarItem[] => {
    if (!query.trim()) return items;
    const q = query.toLowerCase();
    return items
      .map(item => {
        const matchLabel = item.label.toLowerCase().includes(q);

        // Se subItems já carregados, filtra normalmente
        let filteredSub: SideBarItem[] = [];
        const key = getItemKey(item, []);
        if (item.subItems) {
          filteredSub = filterItems(item.subItems, query);
        } else if (loadedSubItems[key]) {
          filteredSub = filterItems(loadedSubItems[key], query);
        }

        // Se item tem loadSubItems e não está carregado, considera para busca
        const hasLazy = !!item.loadSubItems && !item.subItems && !loadedSubItems[key];
        if (matchLabel || filteredSub.length > 0 || hasLazy) {
          return {
            ...item,
            subItems: filteredSub.length > 0 ? filteredSub : item.subItems ?? loadedSubItems[key],
          };
        }
        return null;
      })
      .filter(Boolean) as SideBarItem[];
  };

  // Normaliza itens se vierem no formato legado
  const normalizedItems: SideBarItem[] = isLegacyMenu(items as unknown[])
  ? legacyMenuToSidebar(items as unknown as LegacyMenuItem[])
  : items;

  // Use normalizedItems em vez de items
  const filteredItems = filterItems(normalizedItems, search);

  // Expande/colapsa usando o caminho do item, garantindo apenas um aberto por nível
  const handleExpand = async (key: string, depth: number, item?: SideBarItem) => {
    // Se já está carregando, não faz nada
    if (item?.loadSubItems && !loadedSubItems[key] && !loadingKeys.includes(key)) {
      setLoadingKeys(prev => [...prev, key]);
      try {
        const subItems = await item.loadSubItems();
        setLoadedSubItems(prev => ({ ...prev, [key]: subItems }));
      } finally {
        setLoadingKeys(prev => prev.filter(k => k !== key));
      }
    }
    setExpanded(prev => {
      const next = prev.slice(0, depth);
      if (prev[depth] === key) {
        // Se já está aberto, fecha
        return next;
      }
      next[depth] = key; // abre o novo submenu neste nível
      return next;
    });
  };

  // Fechar com ESC
  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      // Tab trap
      if (e.key === 'Tab' && sidebarRef.current) {
        const focusable = sidebarRef.current.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, onClose]);

  // Foco inicial no primeiro item
  useEffect(() => {
    if (open) {
      setTimeout(() => {
        firstItemRef.current?.focus();
      }, 50);
    }
  }, [open]);

  // Overlay para clicar fora
  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (
        sidebarRef.current &&
        !sidebarRef.current.contains(e.target as Node)
      ) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open, onClose]);

  // Gerenciar overflow do body
  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  // Função recursiva para renderizar itens e subitens
  const renderMenuItems = (
    items: SideBarItem[],
    parentKeys: string[] = [],
    depth: number = 0
  ) => (
    <ul className={depth > 0 ? "ml-6 mt-1 border-l-2 border-blue-200 dark:border-blue-700 pl-2" : ""}>
      {items.map((item, idx) => {
        const key = getItemKey(item, parentKeys);
        const isExpanded = expanded[depth] === key;
        const refProp = depth === 0 && idx === 0 ? { ref: firstItemRef } : {};
        const isLoading = loadingKeys.includes(key);

        // Common props for menu items (buttons/links)
        const commonProps = {
          className: clsx(
            "w-full flex items-center justify-between px-4 py-2 text-left rounded transition font-grotesk",
            isExpanded ? "bg-blue-50 dark:bg-slate-800" : "hover:bg-blue-100 dark:hover:bg-slate-800",
            "text-sm",
            "focus-visible:outline-2 focus-visible:outline-blue-500 focus-visible:outline-offset-2 focus:ring-2 focus:ring-blue-400"
          ),
          tabIndex: 0,
          role: "menuitem",
          "aria-label": item.label,
        };

        // Decide quais subitens mostrar
        let subItemsToShow = item.subItems;
        if (!subItemsToShow && loadedSubItems[key]) {
          subItemsToShow = loadedSubItems[key];
        }

        const content = (
          <>
            <span className="flex items-center gap-2 font-inter">
              {item.icon}
              {item.label}
              {item.badge && (
                <span
                  className={clsx(
                    "ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium font-grotesk",
                    item.badgeClassName ??
                      "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200"
                  )}
                >
                  {item.badge}
                </span>
              )}
            </span>
            {(item.subItems || item.loadSubItems) && (
              <svg
                className={clsx(
                  'ml-2 transition-transform',
                  isExpanded ? 'rotate-90 text-blue-600' : 'text-gray-400',
                  'w-5 h-5'
                )}
                width="20"
                height="20"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                aria-hidden="true"
              >
                <polyline points="6 9 12 15 18 9" />
              </svg>
            )}
          </>
        );

        return (
          <li key={key}>
            {(item.subItems || item.loadSubItems) ? (
              <button
                {...commonProps}
                {...refProp}
                onClick={() => handleExpand(key, depth, item)}
                aria-expanded={!!(item.subItems || item.loadSubItems) && isExpanded}
                type="button"
                disabled={isLoading}
              >
                {content}
                {isLoading && (
                  <span className="ml-2 animate-spin text-blue-500">
                    <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="8" cy="8" r="6" strokeOpacity="0.3" />
                      <path d="M8 2a6 6 0 0 1 6 6" />
                    </svg>
                  </span>
                )}
              </button>
            ) : item.to ? (
              <LinkComponent
                href={item.to}
                {...commonProps}
                onClick={() => {
                  item.onClick?.();
                  onClose();
                }}
                role="menuitem"
                aria-current={item.to === selectedMenu ? "page" : undefined}
              >
                {content}
              </LinkComponent>
            ) : (
              <button
                {...commonProps}
                {...refProp}
                onClick={() => {
                  item.onClick?.();
                  onClose();
                }}
                type="button"
              >
                {content}
              </button>
            )}
            {(item.subItems || loadedSubItems[key]) && isExpanded && !isLoading &&
              renderMenuItems(subItemsToShow ?? [], [...parentKeys, item.label], depth + 1)
            }
          </li>
        );
      })}
    </ul>
  );

  // useCallback para setSearch
  const handleSearchChange = useCallback((value: unknown) => {
    setSearch(typeof value === 'string' ? value : '');
  }, []);

  const LinkComponent = linkComponent ?? (({ href, children, ...rest }) => (
    <a href={href} {...rest}>{children}</a>
  ));

  return (
    <>
      {/* Overlay com transição de opacidade */}
      <div
        className={clsx(
          "fixed inset-0 z-30 bg-black/40 transition-opacity duration-300 ease-in-out",
          open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        )}
        aria-hidden="true"
        onClick={onClose}
      />
      {/* Sidebar com transição de slide */}
      <aside
        ref={sidebarRef}
        className={clsx(
          'fixed inset-y-0 left-0 z-40 w-64 transition-transform duration-300 ease-in-out',
          open ? 'translate-x-0' : '-translate-x-full',
          'border-r backdrop-blur-md',
          {
            'bg-white/60 border-gray-200 text-gray-900 shadow-[0_4px_24px_0_rgba(0,120,212,0.10)]': theme === 'light',
            'bg-[#201f1e]/60 border-[#292827] text-white shadow-[0_4px_24px_0_rgba(255,255,255,0.10)]': theme === 'dark',
          }
        )}
        style={{
          transition: 'background 0.2s, color 0.2s, box-shadow 0.2s',
          boxShadow:
            theme === 'dark'
              ? '0 4px 24px 0 rgba(255,255,255,0.10)'
              : '0 4px 24px 0 rgba(0,120,212,0.10)',
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
        }}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
      >
        <div className="flex flex-col h-full">
          {/* Exibe o logo do cliente no topo, se fornecido */}
          {logoUrl && (
            <div className="flex items-center justify-center py-4">
              <SmartImage
                src={logoUrl}
                alt={logoAlt || "Logo"}
                className="h-12 w-auto max-w-[80%] rounded bg-white shadow"
                width={96}
                height={48}
                layout="fixed"
                draggable={false}
                ariaHidden={false}
              />
            </div>
          )}
          {/* Título oculto para leitores de tela */}
          <h2 id={titleId} className="sr-only">Menu lateral</h2>
          <div className="flex items-center justify-between px-4 py-3">
            <div className="flex-1" />
            <Button
              onClick={onClose}
              theme={theme}
              variant="icon"
              size="tiny"
              ariaLabel="Fechar menu"
              buttonClassName="hover:bg-gray-100 dark:hover:bg-slate-800 transition border-0 p-1 focus-visible:outline-2 focus-visible:outline-blue-500 focus-visible:outline-offset-2"
              icon={
                <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <line x1="5" y1="5" x2="15" y2="15" />
                  <line x1="15" y1="5" x2="5" y2="15" />
                </svg>
              }
            />
          </div>
          {/* Campo de busca */}
          <div className="px-4 py-2">
            <InputField
              type="search"
              value={search}
              onChange={handleSearchChange}
              onFocus={useCallback(() => {}, [])}
              onBlur={useCallback(() => {}, [])}
              placeholder="Buscar menu..."
              leftIcon={
                <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <circle cx="7" cy="7" r="5"/>
                  <line x1="11" y1="11" x2="15" y2="15"/>
                </svg>
              }
              theme={theme}
              inputClassName="text-sm font-grotesk"
              containerProps={{ className: "mb-0" }}
              aria-label="Buscar menu"
            />
          </div>
          <nav
            className="flex-1 overflow-y-auto py-2"
            role="navigation"
            aria-label="Menu lateral"
          >
            {filteredItems.length === 0 ? (
              <ul>
                <li className="px-4 py-2 text-sm text-gray-400 dark:text-gray-500 font-grotesk">Nenhum item encontrado</li>
              </ul>
            ) : (
              renderMenuItems(filteredItems)
            )}
          </nav>
        </div>
      </aside>
    </>
  );
};

// Corrija a definição do schema para evitar recursão infinita
const SideBarItemSchema: z.ZodType<SideBarItem> = z.lazy(() =>
  z.object({
    label: z.string(),
    icon: z.any().optional(),
    onClick: z.function().args().returns(z.void()).optional(),
    to: z.string().optional(),
    subItems: z.array(SideBarItemSchema).optional(),
    badge: z.any().optional(),
    badgeClassName: z.string().optional(),
    loadSubItems: z.function().args().returns(z.promise(z.array(SideBarItemSchema))).optional(),
  }).catchall(z.unknown())
);

export { SideBarItemSchema };

// Função utilitária para normalizar e validar dados genéricos em SideBarItem[]
export function normalizeSidebarData(
  data: unknown[],
  config?: {
    labelKey?: string;
    iconKey?: string;
    toKey?: string;
    onClickKey?: string;
    subItemsKey?: string;
    badgeKey?: string;
    badgeClassNameKey?: string;
  }
): SideBarItem[] {
  const {
    labelKey = 'label',
    iconKey = 'icon',
    toKey = 'to',
    onClickKey = 'onClick',
    subItemsKey = 'subItems',
    badgeKey = 'badge',
    badgeClassNameKey = 'badgeClassName',
  } = config || {};

  if (!Array.isArray(data)) return [];

  // Mapeia e valida cada item usando o schema Zod
  return data.map((item) => {
    if (typeof item !== 'object' || item === null) {
      throw new Error('Invalid item type in normalizeSidebarData');
    }
    const typedItem = item as Record<string, unknown>;
    const raw: Partial<SideBarItem> = {
      label: typedItem[labelKey] as string,
      icon: typedItem[iconKey] as React.ReactNode,
      to: typedItem[toKey] as string | undefined,
      onClick: typedItem[onClickKey] as (() => void) | undefined,
      badge: typedItem[badgeKey] as React.ReactNode,
      badgeClassName: typedItem[badgeClassNameKey] as string | undefined,
      subItems: typedItem[subItemsKey]
        ? normalizeSidebarData(typedItem[subItemsKey] as unknown[], config)
        : undefined,
    };
    // Valida e retorna o item seguro
    return SideBarItemSchema.parse(raw);
  });
}

// Exemplo de uso externo:
// const rawData = await fetch(...);
// const items = normalizeSidebarData(rawData, { labelKey: 'nome', toKey: 'rota', subItemsKey: 'filhos' });
// <SideBar items={items} ... />

export default SideBar;